import { ChevronRightIcon } from 'lucide-react';
import { useId, useState, type JSX, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Card } from '@/components/ui/card.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.js';
import type {
  AnswerJudgement,
  AnswerJudgementChecklistItem,
  AnswerJudgementCriterion,
  AnswerJudgementSummary,
  AnswerJudgeSkillRef,
  RunSummary,
  TestResult,
} from '@/lib/api/quality-types.js';
import { cn } from '@/lib/utils.js';

import { caseDisplayStatus } from '../quality-outcome.js';
import { StatusBadge } from '../quality-presentation.js';
import {
  checklistPercentFrom,
  CRITERION_NAMES,
  evidenceKindLabel,
  nonEmptyText,
  notScoredText,
  objectsIn,
  percentText,
  readAnswerJudgement,
  rubricAverageFrom,
  wholeNumberInRange,
} from './AnswerJudgement.js';

/** What a value the run lacks, or the contract rules out, shows as. */
const NOT_AVAILABLE = 'n/a';

/** What a model the run never recorded shows as. */
const NOT_SET = 'Not set';

/** The five rubric criteria in the order the report shows them, with each chip's short label. */
const CRITERION_CHIPS = [
  { name: 'answers_question', shortLabel: 'Ans' },
  { name: 'follows_format', shortLabel: 'Fmt' },
  { name: 'stays_grounded', shortLabel: 'Grd' },
  { name: 'clarity', shortLabel: 'Clr' },
  { name: 'language_and_tone', shortLabel: 'Tone' },
] as const;

/** The Judged cases table's column headers, in order. */
const JUDGED_CASE_COLUMNS = [
  'Case',
  'Pass/fail',
  'Quality skill %',
  'Facts skill %',
  'Answer time',
  'Judge model',
  'Model under test',
  'Evidence',
];

/** A sha-256 digest in lowercase hex, the only shape a skill revision is read from. */
const SHA256_HEX = /^[0-9a-f]{64}$/;

/** Shared look of the report's small section headings, matching the Cases card header. */
const SECTION_HEADING_CLASS =
  'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

/** A run result the answer judge ruled on, with its judgement already read. */
interface JudgedCase {
  result: TestResult;
  judgement: AnswerJudgement;
}

/** One label and value row of the Models block; the hint is a muted note after the value. */
interface ModelRowContent {
  label: string;
  value: string;
  hint?: string | null;
}

/** One Summary tile: the value, an optional muted tail such as " / 3", and a red alert tone. */
interface SummaryTileContent {
  label: string;
  value: string;
  detail?: string;
  alert?: boolean;
}

/** The results that carry a judgement this build can read, in the cases list's order. */
function judgedCasesOf(results: TestResult[]): JudgedCase[] {
  return results.flatMap((result) => {
    const judgement = readAnswerJudgement(result.answer_judgement);
    return judgement ? [{ result, judgement }] : [];
  });
}

/** "90%": a 1–5 rubric average as a whole percent of the top score, or n/a. */
function qualityPercentText(value: unknown): string {
  const average = rubricAverageFrom(value);
  return average === null
    ? NOT_AVAILABLE
    : `${Math.round((average / 5) * 100)}%`;
}

/** "18.4 s": a finite, non-negative answer time in milliseconds as seconds with one decimal, or n/a. */
function answerTimeText(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? `${(value / 1000).toFixed(1)} s`
    : NOT_AVAILABLE;
}

/** "83%": a checklist share rounded to a whole percent, or n/a. */
function factsPercentText(value: unknown): string {
  const percent = checklistPercentFrom(value);
  return percent === null ? NOT_AVAILABLE : percentText(percent);
}

/** Scored and judged case counts, when both are whole numbers and scored fits within judged. */
function scoredOfJudged(
  summary: AnswerJudgementSummary,
): { scored: number; judged: number } | null {
  const judged = wholeNumberInRange(summary.judged_cases, 0);
  const scored = wholeNumberInRange(summary.scored_cases, 0);
  return judged !== null && scored !== null && scored <= judged
    ? { scored, judged }
    : null;
}

/** Cases with a critical miss: they are scored cases, so the count never exceeds the scored count. */
function criticalMissCount(summary: AnswerJudgementSummary): number | null {
  const scored = wholeNumberInRange(summary.scored_cases, 0);
  return wholeNumberInRange(summary.critical_miss_cases, 0, scored ?? 0);
}

/**
 * "Quality skill 90% · Facts skill 83% · median answer 18.4 s · 1 critical miss · 2 of 2 scored" for
 * the collapsed row; the answer time only when the run has one.
 */
function headerSummaryText(summary: AnswerJudgementSummary): string {
  const criticalMisses = criticalMissCount(summary);
  const scored = scoredOfJudged(summary);
  const medianAnswer = answerTimeText(summary.answer_ms_median);
  return [
    `Quality skill ${qualityPercentText(summary.rubric_average)}`,
    `Facts skill ${factsPercentText(summary.checklist_percent)}`,
    medianAnswer === NOT_AVAILABLE ? null : `median answer ${medianAnswer}`,
    criticalMisses
      ? `${criticalMisses} critical miss${criticalMisses === 1 ? '' : 'es'}`
      : null,
    scored ? `${scored.scored} of ${scored.judged} scored` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

/** Passed over total cases from the run summary. A canonical run counts passes under `results`. */
function casesPassed(
  runSummary: RunSummary | undefined,
): { passed: number; total: number } | null {
  const total = wholeNumberInRange(runSummary?.total, 0);
  if (total === null) return null;
  const passed = wholeNumberInRange(
    runSummary?.results?.passed ?? runSummary?.passed,
    0,
    total,
  );
  return passed === null ? null : { passed, total };
}

/** The six Summary tiles, each falling back to n/a for a value the run lacks. */
function summaryTiles(
  summary: AnswerJudgementSummary,
  runSummary: RunSummary | undefined,
): SummaryTileContent[] {
  const passed = casesPassed(runSummary);
  const scored = scoredOfJudged(summary);
  const criticalMisses = criticalMissCount(summary);
  return [
    passed
      ? {
          label: 'Cases passed',
          value: `${passed.passed}`,
          detail: ` / ${passed.total}`,
        }
      : { label: 'Cases passed', value: NOT_AVAILABLE },
    {
      label: 'Quality skill (avg)',
      value: qualityPercentText(summary.rubric_average),
    },
    {
      label: 'Facts skill (avg)',
      value: factsPercentText(summary.checklist_percent),
    },
    {
      label: 'Cases with a critical miss',
      value: criticalMisses === null ? NOT_AVAILABLE : `${criticalMisses}`,
      alert: (criticalMisses ?? 0) > 0,
    },
    scored
      ? {
          label: 'Cases scored',
          value: `${scored.scored}`,
          detail: ` of ${scored.judged}`,
        }
      : { label: 'Cases scored', value: NOT_AVAILABLE },
    answerTimeTile(summary),
  ];
}

/** The median answer time, with the 90th percentile beside it when the service sent one. */
function answerTimeTile(summary: AnswerJudgementSummary): SummaryTileContent {
  const p90 = answerTimeText(summary.answer_ms_p90);
  return {
    label: 'Answer time (median)',
    value: answerTimeText(summary.answer_ms_median),
    ...(p90 === NOT_AVAILABLE ? {} : { detail: ` · p90 ${p90}` }),
  };
}

/** Each distinct value once, comma-joined in first-seen order; a missing one reads Not set. */
function distinctValues(values: (string | null)[]): string {
  return (
    [...new Set(values.map((value) => value ?? NOT_SET))].join(', ') || NOT_SET
  );
}

/** The judgement's skills when the cast field holds an object, else null. */
function skillsOf(
  judgement: AnswerJudgement,
): NonNullable<AnswerJudgement['skills']> | null {
  const skills: unknown = judgement.skills;
  return skills && typeof skills === 'object' && !Array.isArray(skills)
    ? (skills as NonNullable<AnswerJudgement['skills']>)
    : null;
}

/** "rev 98e657f, 1c2d3e4": the first seven characters of each distinct well-formed digest. */
function revisionHint(skillRefs: unknown[]): string | null {
  const revisions = objectsIn<AnswerJudgeSkillRef>(skillRefs)
    .map((skillRef) => skillRef.digest)
    .filter(
      (digest): digest is string =>
        typeof digest === 'string' && SHA256_HEX.test(digest),
    )
    .map((digest) => digest.slice(0, 7));
  const distinct = [...new Set(revisions)];
  return distinct.length > 0 ? `rev ${distinct.join(', ')}` : null;
}

/** The Facts skill row: its revisions, Not used when every case lacked a checklist, else nothing. */
function factsSkillRow(factsRefs: unknown[]): ModelRowContent | null {
  if (objectsIn(factsRefs).length > 0)
    return {
      label: 'Facts skill',
      value: '/judge-answer-facts',
      hint: revisionHint(factsRefs),
    };
  return factsRefs.every((factsRef) => factsRef === null)
    ? { label: 'Facts skill', value: 'Not used (no checklist)' }
    : null;
}

/** The Quality skill and Facts skill rows, or none when no judgement says which skills it ran. */
function skillRows(judgements: AnswerJudgement[]): ModelRowContent[] {
  const skillsList = judgements
    .map(skillsOf)
    .filter(
      (skills): skills is NonNullable<AnswerJudgement['skills']> =>
        skills !== null,
    );
  if (skillsList.length === 0) return [];
  const factsRow = factsSkillRow(skillsList.map((skills) => skills.facts));
  return [
    {
      label: 'Quality skill',
      value: '/judge-answer-quality',
      hint: revisionHint(skillsList.map((skills) => skills.quality)),
    },
    ...(factsRow ? [factsRow] : []),
  ];
}

/** The Models block's rows: the models across the judged cases, the tester model and the skills. */
function modelRows(
  judgements: AnswerJudgement[],
  testerModel: string | null | undefined,
): ModelRowContent[] {
  const tester = nonEmptyText(testerModel);
  return [
    {
      label: 'Model under test',
      value: distinctValues(
        judgements.map((judgement) => nonEmptyText(judgement.agent_models)),
      ),
      hint: 'AGENT_MODELS',
    },
    {
      label: 'Judge model',
      value: distinctValues(
        judgements.map((judgement) => nonEmptyText(judgement.judge_model)),
      ),
    },
    ...(tester
      ? [
          {
            label: 'Tester model',
            value: tester,
            hint: 'drives the browser, not judged',
          },
        ]
      : []),
    ...skillRows(judgements),
  ];
}

/** A titled block of the expanded report, named by its heading for assistive tech. */
function ReportSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="min-w-0 space-y-2">
      <h3 id={headingId} className={SECTION_HEADING_CLASS}>
        {title}
      </h3>
      {children}
    </section>
  );
}

/** The Models block: what was tested, what judged it, what drove the browser, which skills ran. */
function ModelsBlock({ rows }: { rows: ModelRowContent[] }): JSX.Element {
  return (
    <ReportSection title="Models">
      <dl className="grid grid-cols-[minmax(7rem,auto)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
        {rows.map((row) => (
          <div key={row.label} className="contents">
            <dt className="text-muted-foreground">{row.label}</dt>
            <dd className="min-w-0 break-words font-medium">
              {row.value}
              {row.hint ? (
                <>
                  {' '}
                  <span className="inline-block text-xs font-normal text-muted-foreground">
                    {row.hint}
                  </span>
                </>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </ReportSection>
  );
}

/** The Summary block: six tiles, label under value, the critical miss count red when above 0. */
function SummaryBlock({ tiles }: { tiles: SummaryTileContent[] }): JSX.Element {
  return (
    <ReportSection title="Summary">
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="flex flex-col-reverse justify-end rounded-lg bg-muted/40 px-3 py-2"
          >
            <dt className="text-xs text-muted-foreground">{tile.label}</dt>
            <dd
              className={cn(
                'font-heading text-xl font-bold tabular-nums',
                tile.alert && 'text-destructive',
              )}
            >
              {tile.value}
              {tile.detail ? (
                <span className="text-sm font-medium text-muted-foreground">
                  {tile.detail}
                </span>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>
    </ReportSection>
  );
}

/** A skill cell for a case the judge could not score: Not scored and the reason. */
function NotScoredCell({
  judgement,
}: {
  judgement: AnswerJudgement;
}): JSX.Element {
  return (
    <>
      <div className="font-medium text-muted-foreground">Not scored</div>
      <div className="text-xs text-muted-foreground">
        {notScoredText(judgement.not_scored_reason)}
      </div>
    </>
  );
}

/** The case's valid criterion scores in the fixed chip order; a malformed one is left out. */
function criterionScores(
  judgement: AnswerJudgement,
): { name: string; shortLabel: string; score: number }[] {
  const criteria = objectsIn<AnswerJudgementCriterion>(judgement.criteria);
  return CRITERION_CHIPS.flatMap((chip) => {
    const criterion = criteria.find((each) => each.name === chip.name);
    const score = wholeNumberInRange(criterion?.score, 1, 5);
    const name = CRITERION_NAMES[chip.name] ?? chip.name;
    return score === null ? [] : [{ name, shortLabel: chip.shortLabel, score }];
  });
}

/** The Quality skill cell: the percent, the points out of 25 and one chip per criterion. */
function QualityCell({
  judgement,
}: {
  judgement: AnswerJudgement;
}): JSX.Element {
  const scores = criterionScores(judgement);
  const points = scores.reduce((sum, criterion) => sum + criterion.score, 0);
  return (
    <>
      <div className="font-heading text-base font-semibold tabular-nums">
        {qualityPercentText(judgement.rubric_average)}
      </div>
      {scores.length === CRITERION_CHIPS.length ? (
        <div className="text-xs text-muted-foreground">{`${points} of 25 points`}</div>
      ) : null}
      {scores.length > 0 ? (
        <ul className="mt-1 flex flex-wrap gap-1">
          {scores.map((criterion) => (
            <li
              key={criterion.shortLabel}
              title={criterion.name}
              aria-label={`${criterion.name}: ${criterion.score} of 5`}
              className="inline-flex items-baseline gap-1 rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[11px] tabular-nums"
            >
              <span className="text-muted-foreground">
                {criterion.shortLabel}
              </span>
              <span className="font-semibold">{criterion.score}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/** "4 of 6 met · 1 unclear": met over the items not unclear; an unknown result is left out. */
function checklistCountText(checklist: AnswerJudgementChecklistItem[]): string {
  const count = (result: string): number =>
    checklist.filter((item) => item.result === result).length;
  const met = count('met');
  const unclear = count('unclear');
  const unclearText = unclear > 0 ? ` · ${unclear} unclear` : '';
  return `${met} of ${met + count('not_met')} met${unclearText}`;
}

/** The critical miss verdict as a badge; nothing when the field is not a boolean. */
function CriticalMissBadge({ value }: { value: unknown }): JSX.Element | null {
  if (value === true)
    return (
      <Badge variant="destructive" className="mt-1">
        Critical miss
      </Badge>
    );
  if (value === false)
    return (
      <Badge variant="success" className="mt-1">
        No critical miss
      </Badge>
    );
  return null;
}

/** The Facts skill cell: the percent, the met count and the critical miss verdict. */
function FactsCell({ judgement }: { judgement: AnswerJudgement }): JSX.Element {
  const checklist = objectsIn<AnswerJudgementChecklistItem>(
    judgement.checklist,
  );
  if (checklist.length === 0)
    return <span className="text-muted-foreground">No checklist</span>;
  return (
    <>
      <div className="font-heading text-base font-semibold tabular-nums">
        {factsPercentText(judgement.checklist_percent)}
      </div>
      <div className="text-xs text-muted-foreground">
        {checklistCountText(checklist)}
      </div>
      <CriticalMissBadge value={judgement.critical_miss} />
    </>
  );
}

/** A model name from the judgement, or a muted Not set. */
function ModelName({ value }: { value: unknown }): JSX.Element {
  const name = nonEmptyText(value);
  return name ? (
    <span className="break-words">{name}</span>
  ) : (
    <span className="text-muted-foreground">{NOT_SET}</span>
  );
}

/** What the judge read, as a small muted tag; nothing when it read nothing. */
function EvidenceTag({ value }: { value: unknown }): JSX.Element | null {
  const label = evidenceKindLabel(value);
  return label ? (
    <Badge variant="outline" className="font-normal text-muted-foreground">
      {label}
    </Badge>
  ) : null;
}

/** One judged case: who it is, how it ended, both skill scores, the answer time, the models and the evidence. */
function JudgedCaseRow({
  judgedCase: { result, judgement },
}: {
  judgedCase: JudgedCase;
}): JSX.Element {
  const notScored = judgement.status === 'not_scored';
  const title = nonEmptyText(result.title);
  return (
    <tr className="align-top">
      <td className="px-3 py-2.5">
        <div className="text-xs font-semibold text-primary [[data-theme=dark]_&]:text-violet-300">
          {result.case_id}
        </div>
        {title ? <div className="break-words">{title}</div> : null}
      </td>
      <td className="px-3 py-2.5">
        <StatusBadge status={caseDisplayStatus(result)} />
      </td>
      <td className="px-3 py-2.5">
        {notScored ? (
          <NotScoredCell judgement={judgement} />
        ) : (
          <QualityCell judgement={judgement} />
        )}
      </td>
      <td className="px-3 py-2.5">
        {notScored ? (
          <NotScoredCell judgement={judgement} />
        ) : (
          <FactsCell judgement={judgement} />
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 tabular-nums">
        {answerTimeText(judgement.answer_ms)}
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-xs">
        <ModelName value={judgement.judge_model} />
      </td>
      <td className="px-3 py-2.5 text-xs">
        <ModelName value={judgement.agent_models} />
      </td>
      <td className="px-3 py-2.5">
        <EvidenceTag value={judgement.evidence_kind} />
      </td>
    </tr>
  );
}

/** The Judged cases table. It keeps a minimum width and scrolls sideways inside its box on a phone. */
function JudgedCasesTable({
  judgedCases,
}: {
  judgedCases: JudgedCase[];
}): JSX.Element {
  const headingId = useId();
  return (
    <section className="min-w-0 space-y-2">
      <h3 id={headingId} className={SECTION_HEADING_CLASS}>
        Judged cases
      </h3>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table
          aria-labelledby={headingId}
          className="w-full min-w-[62rem] text-sm"
        >
          <thead className="bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              {JUDGED_CASE_COLUMNS.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="px-3 py-2.5 font-medium"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {judgedCases.map((judgedCase) => (
              <JudgedCaseRow
                key={judgedCase.result.case_id}
                judgedCase={judgedCase}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * The answer judge report under a run's cases list (MEDA-943): a collapsed row with the run's answer
 * scores that opens to the models, six summary tiles and a table of the judged cases. Renders
 * nothing for a run the answer judge did not see.
 */
export function AnswerJudgeReport({
  judgementSummary,
  results,
  runSummary,
  testerModel,
}: {
  judgementSummary: AnswerJudgementSummary | null | undefined;
  results: TestResult[];
  runSummary: RunSummary | undefined;
  testerModel: string | null | undefined;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (
    !judgementSummary ||
    typeof judgementSummary !== 'object' ||
    Array.isArray(judgementSummary)
  )
    return null;
  const judgedCases = judgedCasesOf(results);
  const judgements = judgedCases.map((judgedCase) => judgedCase.judgement);
  return (
    <Card className="gap-0 overflow-hidden">
      <Collapsible open={open} onOpenChange={setOpen}>
        <h2>
          <CollapsibleTrigger className="flex w-full items-start gap-2 px-4 py-2.5 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
            <ChevronRightIcon
              aria-hidden
              className={cn(
                'mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform',
                open && 'rotate-90',
              )}
            />
            <span className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-3">
              <span className={SECTION_HEADING_CLASS}>Answer judge report</span>
              <span className="text-xs font-normal text-muted-foreground">
                {headerSummaryText(judgementSummary)}
              </span>
            </span>
          </CollapsibleTrigger>
        </h2>
        <CollapsibleContent>
          <div className="space-y-5 border-t border-border p-4">
            <div className="grid gap-5 lg:grid-cols-2">
              <ModelsBlock rows={modelRows(judgements, testerModel)} />
              <SummaryBlock
                tiles={summaryTiles(judgementSummary, runSummary)}
              />
            </div>
            <JudgedCasesTable judgedCases={judgedCases} />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
